import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  IconPlayerPlay,
  IconPlus,
  IconLoader2,
  IconCalendar,
  IconFilter,
  IconDatabaseImport,
  IconArrowLeft,
  IconBolt,
  IconPackage,
  IconChevronDown,
  IconChevronRight,
  IconCheck,
  IconSend,
  IconEdit,
  IconCurrencyDollar,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import { Textarea } from "@/components/ui/textarea"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { api } from "@/lib/api"

// ─── Types ────────────────────────────────────────────────────────────────────

interface BenefitRunSummary {
  id: number
  benefitProgramYear: number
  runNumber: number
  startDate: string
  endDate: string
  createdAt: string
  createdBy: string
  notes: string | null
}

interface CreateBenefitRunRequest {
  benefitProgramYear: number
  runNumber?: number
  startDate: string
  endDate: string
  notes?: string
}

interface UpdateBenefitRunRequest {
  startDate: string
  endDate: string
  notes?: string
}

interface ImportLegacyRunsResult {
  totalLegacyRows: number
  imported: number
  skipped: number
}

interface PsePayListSummary {
  id: number
  benefitRunId: number
  runNumber: number
  benefitProgramYear: number
  batchNumber: number
  isAmiOptOut: boolean
  maxBatchSize: number
  totalPaymentCount: number
  totalAmount: number
  status: number
  createdAt: string
  createdBy: string
  notes: string | null
}

interface PsePayListDetail extends PsePayListSummary {
  items: PsePayListItemDto[]
}

interface PsePayListItemDto {
  id: number
  benefitId: string
  benefitProgramId: number
  benefitProgramYear: number
  vendorId: number
  accountNumber: string
  paymentAmount: number
  sortOrder: number
}

interface PsePaymentListV2Capability {
  enabled: boolean
  disabledReason: string | null
  supportedPrograms: string[]
}

interface PsePaymentListV2List {
  listId: string
  batchNumber: number
  isAmiOptOut: boolean
  totalPaymentCount: number
  totalAmount: number
  distinctHouseholdCount: number
}

interface PsePaymentListV2Operation {
  operationId: string
  operationKey: string
  state: string
  benefitRunId: number
  runNumber: number
  runStartDate: string
  runEndDate: string
  benefitProgramId: number
  benefitProgramYear: number
  maxBatchSize: number
  candidateCount: number
  inputHash: string
  explicitReExport: boolean
  artifactFileName: string | null
  artifactSize: number | null
  createdAt: string
  committedAt: string | null
  failureCode: string | null
  lists: PsePaymentListV2List[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatDateRange(start: string, end: string): string {
  const s = formatDate(start)
  const e = formatDate(end)
  return s === e ? s : `${s} – ${e}`
}

function toInputDate(iso: string): string {
  return iso.slice(0, 10)
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

function RunNumberBadge({ year, run }: { year: number; run: number }) {
  return (
    <Badge variant="secondary" className="font-mono text-xs">
      {year}-{String(run).padStart(3, "0")}
    </Badge>
  )
}

const payListStatusLabels: Record<number, string> = {
  0: "Draft",
  1: "Finalized",
  2: "Submitted",
  3: "Modified",
}

function PayListStatusBadge({ status }: { status: number }) {
  const label = payListStatusLabels[status] ?? `Unknown (${status})`
  switch (status) {
    case 0:
      return <Badge variant="outline">{label}</Badge>
    case 1:
      return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {label}
        </Badge>
      )
    case 2:
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          {label}
        </Badge>
      )
    case 3:
      return (
        <Badge variant="destructive">{label}</Badge>
      )
    default:
      return <Badge variant="outline">{label}</Badge>
  }
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + 1 - i)

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BenefitRunsPage() {
  const { runId } = useParams<{ runId: string }>()

  if (runId) {
    return <RunDetailView runId={Number(runId)} />
  }
  return <RunListView />
}

// ═══════════════════════════════════════════════════════════════════════════════
// Run List View
// ═══════════════════════════════════════════════════════════════════════════════

function RunListView() {
  const navigate = useNavigate()
  const [runs, setRuns] = useState<BenefitRunSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [yearFilter, setYearFilter] = useState<string>("all")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<ImportLegacyRunsResult | null>(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  // Create form state
  const [formYear, setFormYear] = useState(currentYear.toString())
  const [formStartDate, setFormStartDate] = useState("")
  const [formEndDate, setFormEndDate] = useState("")
  const [formNotes, setFormNotes] = useState("")

  const fetchRuns = useCallback(async (year?: string) => {
    setLoading(true)
    setError(null)
    try {
      const query = year && year !== "all" ? `?BenefitProgramYear=${year}` : ""
      const data = await api.get<BenefitRunSummary[]>(`/api/benefit-runs${query}`)
      setRuns(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch runs")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRuns(yearFilter)
  }, [fetchRuns, yearFilter])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    setCreateError(null)
    try {
      const body: CreateBenefitRunRequest = {
        benefitProgramYear: Number(formYear),
        startDate: formStartDate,
        endDate: formEndDate,
        notes: formNotes || undefined,
      }
      await api.post<BenefitRunSummary>("/api/benefit-runs", body)
      setDialogOpen(false)
      resetForm()
      fetchRuns(yearFilter)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create run")
    } finally {
      setCreating(false)
    }
  }

  const handleImport = async () => {
    setImporting(true)
    setImportError(null)
    setImportResult(null)
    try {
      const result = await api.post<ImportLegacyRunsResult>("/api/benefit-runs/import-legacy")
      setImportResult(result)
      fetchRuns(yearFilter)
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Failed to import legacy runs")
    } finally {
      setImporting(false)
    }
  }

  const resetForm = () => {
    setFormYear(currentYear.toString())
    setFormStartDate("")
    setFormEndDate("")
    setFormNotes("")
    setCreateError(null)
  }

  const totalRuns = runs.length
  const thisYearRuns = runs.filter((r) => r.benefitProgramYear === currentYear).length
  const latestRun = runs.length > 0 ? runs[runs.length - 1] : null

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <IconPlayerPlay className="size-6" />
            <h1 className="text-2xl font-bold">Benefit Runs</h1>
          </div>
          <p className="mt-1 text-muted-foreground">
            Create and manage benefit payment runs. Each run defines a date range for processing benefit payments.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleImport} disabled={importing}>
            {importing ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconDatabaseImport className="size-4" />
            )}
            Import Legacy
          </Button>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open)
              if (!open) resetForm()
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <IconPlus className="size-4" />
                New Run
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
              <form onSubmit={handleCreate}>
                <DialogHeader>
                  <DialogTitle>Create Benefit Run</DialogTitle>
                  <DialogDescription>
                    Define the program year, date range, and optional notes. The run number is auto-assigned.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="form-year">Program Year</Label>
                    <Select value={formYear} onValueChange={setFormYear}>
                      <SelectTrigger id="form-year">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="form-start">Start Date</Label>
                      <Input id="form-start" type="date" required value={formStartDate} onChange={(e) => setFormStartDate(e.target.value)} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="form-end">End Date</Label>
                      <Input id="form-end" type="date" required value={formEndDate} onChange={(e) => setFormEndDate(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="form-notes">Notes (optional)</Label>
                    <Textarea id="form-notes" placeholder="Any additional notes…" rows={3} value={formNotes} onChange={(e) => setFormNotes(e.target.value)} />
                  </div>
                  {createError && <p className="text-sm text-destructive">{createError}</p>}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating}>
                    {creating && <IconLoader2 className="size-4 animate-spin" />}
                    Create Run
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <Alert>
          <IconDatabaseImport className="size-4" />
          <AlertTitle>Import Complete</AlertTitle>
          <AlertDescription>
            Found {importResult.totalLegacyRows} legacy rows. Imported {importResult.imported}, skipped {importResult.skipped} (already exist).
          </AlertDescription>
        </Alert>
      )}
      {importError && (
        <Alert variant="destructive">
          <AlertTitle>Import Failed</AlertTitle>
          <AlertDescription>{importError}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Runs</div>
            <div className="mt-1 text-2xl font-bold">{totalRuns}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">{currentYear} Runs</div>
            <div className="mt-1 text-2xl font-bold">{thisYearRuns}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Latest Run</div>
            <div className="mt-1 text-lg font-semibold">
              {latestRun ? (
                <span className="flex items-center gap-2">
                  <RunNumberBadge year={latestRun.benefitProgramYear} run={latestRun.runNumber} />
                  <span className="text-sm text-muted-foreground font-normal">{formatDate(latestRun.startDate)}</span>
                </span>
              ) : (
                <span className="text-muted-foreground text-sm font-normal">No runs yet</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <IconFilter className="size-4 text-muted-foreground" />
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {yearOptions.map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {runs.length} run{runs.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={() => fetchRuns(yearFilter)}>Retry</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Run</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-[160px]">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/benefit-runs/${run.id}`)}
                  >
                    <TableCell>
                      <RunNumberBadge year={run.benefitProgramYear} run={run.runNumber} />
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-sm">
                        <IconCalendar className="size-3.5 text-muted-foreground" />
                        {formatDateRange(run.startDate, run.endDate)}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">
                      {run.notes || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(run.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
                {runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                      <div className="flex flex-col items-center gap-2">
                        <IconPlayerPlay className="size-8 text-muted-foreground/50" />
                        <p>No benefit runs found.</p>
                        <p className="text-xs">Create a new run or import legacy runs to get started.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Run Detail View (with PSE Pay Lists panel)
// ═══════════════════════════════════════════════════════════════════════════════

function RunDetailView({ runId }: { runId: number }) {
  const navigate = useNavigate()
  const [run, setRun] = useState<BenefitRunSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pay lists state
  const [payLists, setPayLists] = useState<PsePayListSummary[]>([])
  const [payListsLoading, setPayListsLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // Expanded pay list detail
  const [expandedPayListId, setExpandedPayListId] = useState<number | null>(null)
  const [payListDetail, setPayListDetail] = useState<PsePayListDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  // Durable V2 payment-list state. This is intentionally separate from legacy draft lists.
  const [paymentListV2Capability, setPaymentListV2Capability] = useState<PsePaymentListV2Capability | null>(null)
  const [paymentListV2Operation, setPaymentListV2Operation] = useState<PsePaymentListV2Operation | null>(null)
  const [paymentListV2History, setPaymentListV2History] = useState<PsePaymentListV2Operation[]>([])
  const [paymentListV2OperationKey, setPaymentListV2OperationKey] = useState<string>(() => crypto.randomUUID())
  const [paymentListV2BatchSize, setPaymentListV2BatchSize] = useState("220")
  const [paymentListV2ExplicitReExport, setPaymentListV2ExplicitReExport] = useState(false)
  const [paymentListV2Loading, setPaymentListV2Loading] = useState(false)
  const [paymentListV2Error, setPaymentListV2Error] = useState<string | null>(null)
  const paymentListV2InFlight = useRef(false)
  const registerPaymentListV2 = useBenefitEditRegistration()

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editStartDate, setEditStartDate] = useState("")
  const [editEndDate, setEditEndDate] = useState("")
  const [editNotes, setEditNotes] = useState("")
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const paymentListV2ScopeBusy = editDialogOpen || editing || generating

  const fetchRun = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.get<BenefitRunSummary>(`/api/benefit-runs/${runId}`)
      setRun(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch run")
    } finally {
      setLoading(false)
    }
  }, [runId])

  const fetchPayLists = useCallback(async () => {
    setPayListsLoading(true)
    try {
      const data = await api.get<PsePayListSummary[]>(`/api/pse-pay-lists?BenefitRunId=${runId}`)
      setPayLists(data)
    } catch {
      // silently fail - pay lists may not exist yet
      setPayLists([])
    } finally {
      setPayListsLoading(false)
    }
  }, [runId])

  const fetchPaymentListV2 = useCallback(async () => {
    try {
      const capability = await api.get<PsePaymentListV2Capability>("/api/pse-pay-lists/v2/capability")
      setPaymentListV2Capability(capability)
      // Capability and operation history are read-only. Loading this page must never advance Paid
      // or prepare a candidate operation.
      const history = await api.get<PsePaymentListV2Operation[]>(`/api/pse-pay-lists/v2/operations?benefitRunId=${runId}`)
      setPaymentListV2History(history)
      setPaymentListV2Operation(history[0] ?? null)
      if (history[0]) {
        setPaymentListV2OperationKey(history[0].operationKey)
        setPaymentListV2BatchSize(String(history[0].maxBatchSize))
        setPaymentListV2ExplicitReExport(history[0].explicitReExport)
      }
    } catch {
      // Capability/readback is best effort on the run page; legacy list behavior is unchanged.
    }
  }, [runId])

  useEffect(() => {
    fetchRun()
    fetchPayLists()
    fetchPaymentListV2()
  }, [fetchRun, fetchPayLists, fetchPaymentListV2])

  const runPaymentListV2Action = async (action: () => Promise<void>, fallback: string) => {
    if (paymentListV2InFlight.current) return
    if (paymentListV2ScopeBusy) {
      setPaymentListV2Error("Finish editing the run or generating legacy lists before starting a V2 operation.")
      return
    }
    if (!registerPaymentListV2(true)) {
      setPaymentListV2Error("Save or cancel the open editor before generating a payment list.")
      return
    }
    paymentListV2InFlight.current = true
    setPaymentListV2Loading(true)
    setPaymentListV2Error(null)
    try {
      await action()
    } catch (err) {
      // Keep the operation key untouched. A timeout or lost response can be safely retried
      // with the same key and the server will return the already-created immutable operation.
      setPaymentListV2Error(err instanceof Error ? err.message : fallback)
    } finally {
      paymentListV2InFlight.current = false
      setPaymentListV2Loading(false)
      registerPaymentListV2(false)
    }
  }

  const generatePaymentListV2 = async () => {
    const maxBatchSize = Number(paymentListV2BatchSize)
    if (!paymentListV2OperationKey.trim() || !Number.isInteger(maxBatchSize) || maxBatchSize < 1 || maxBatchSize > 1000) {
      setPaymentListV2Error("Enter an operation key and a batch size from 1 to 1000.")
      return
    }
    await runPaymentListV2Action(async () => {
      const operation = await api.post<PsePaymentListV2Operation>("/api/pse-pay-lists/v2/operations", {
        benefitRunId: runId,
        operationKey: paymentListV2OperationKey.trim(),
        maxBatchSize,
        explicitReExport: paymentListV2ExplicitReExport,
      })
      setPaymentListV2Operation(operation)
      setPaymentListV2History(history => [operation, ...history.filter(item => item.operationId !== operation.operationId)])
    }, "Failed to generate the durable payment list")
  }

  const retryPaymentListV2 = async () => {
    if (!paymentListV2Operation) return
    await runPaymentListV2Action(async () => {
      const operation = await api.post<PsePaymentListV2Operation>(
        `/api/pse-pay-lists/v2/operations/${paymentListV2Operation.operationId}/retry`, {})
      setPaymentListV2Operation(operation)
      setPaymentListV2History(history => [operation, ...history.filter(item => item.operationId !== operation.operationId)])
    }, "Failed to retry the durable payment list")
  }

  const refreshPaymentListV2 = async () => {
    if (!paymentListV2Operation) return
    await runPaymentListV2Action(async () => {
      const operation = await api.get<PsePaymentListV2Operation>(
        `/api/pse-pay-lists/v2/operations/${paymentListV2Operation.operationId}`)
      setPaymentListV2Operation(operation)
      setPaymentListV2History(history => [operation, ...history.filter(item => item.operationId !== operation.operationId)])
    }, "Failed to refresh the durable payment list")
  }

  const startNewPaymentListV2Operation = () => {
    if (paymentListV2InFlight.current) return
    setPaymentListV2Operation(null)
    setPaymentListV2OperationKey(crypto.randomUUID())
    setPaymentListV2ExplicitReExport(false)
    setPaymentListV2BatchSize("220")
    setPaymentListV2Error(null)
  }

  const handleGenerate = async () => {
    if (paymentListV2InFlight.current) return
    setGenerating(true)
    setGenerateError(null)
    try {
      await api.post<PsePayListSummary[]>("/api/pse-pay-lists/generate", {
        benefitRunId: runId,
        maxBatchSize: 220,
      })
      await fetchPayLists()
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : "Failed to generate pay lists")
    } finally {
      setGenerating(false)
    }
  }

  const [updatingPayListId, setUpdatingPayListId] = useState<number | null>(null)

  const handleStatusUpdate = async (payListId: number, newStatus: number) => {
    setUpdatingPayListId(payListId)
    try {
      await api.patch(`/api/pse-pay-lists/${payListId}/status`, { status: newStatus })
      await fetchPayLists()
      if (expandedPayListId === payListId) {
        await loadPayListDetail(payListId)
      }
    } catch (err) {
      console.error("Failed to update status:", err)
    } finally {
      setUpdatingPayListId(null)
    }
  }

  const loadPayListDetail = async (payListId: number) => {
    if (expandedPayListId === payListId) {
      setExpandedPayListId(null)
      setPayListDetail(null)
      return
    }
    setExpandedPayListId(payListId)
    setDetailLoading(true)
    try {
      const detail = await api.get<PsePayListDetail>(`/api/pse-pay-lists/${payListId}`)
      setPayListDetail(detail)
    } catch {
      setPayListDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const openEditDialog = () => {
    if (!run || paymentListV2InFlight.current) return
    setEditStartDate(toInputDate(run.startDate))
    setEditEndDate(toInputDate(run.endDate))
    setEditNotes(run.notes ?? "")
    setEditError(null)
    setEditDialogOpen(true)
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (paymentListV2InFlight.current) return
    setEditing(true)
    setEditError(null)
    try {
      const body: UpdateBenefitRunRequest = {
        startDate: editStartDate,
        endDate: editEndDate,
        notes: editNotes || undefined,
      }
      const updated = await api.put<BenefitRunSummary>(`/api/benefit-runs/${runId}`, body)
      setRun(updated)
      setEditDialogOpen(false)
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to update run")
    } finally {
      setEditing(false)
    }
  }

  const canEdit = payLists.length === 0 && !payListsLoading

  if (loading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !run) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Button variant="ghost" size="sm" className="w-fit" onClick={() => navigate("/benefit-runs")}>
          <IconArrowLeft className="size-4" /> Back to Runs
        </Button>
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm text-destructive">{error ?? "Run not found"}</p>
          <Button variant="outline" size="sm" onClick={fetchRun}>Retry</Button>
        </div>
      </div>
    )
  }

  const totalPayments = payLists.reduce((s, p) => s + p.totalPaymentCount, 0)
  const totalAmount = payLists.reduce((s, p) => s + p.totalAmount, 0)

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Back + Header */}
      <div>
        <Link
          to="/benefit-runs"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <IconArrowLeft className="size-3.5" /> Back to Runs
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                Run <RunNumberBadge year={run.benefitProgramYear} run={run.runNumber} />
              </h1>
            </div>
            <p className="mt-1 text-muted-foreground">
              {formatDateRange(run.startDate, run.endDate)}
              {run.notes && <span className="ml-2">· {run.notes}</span>}
            </p>
          </div>
          {canEdit && (
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={openEditDialog} disabled={paymentListV2Loading}>
                  <IconEdit className="size-4" />
                  Edit Dates
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[480px]">
                <form onSubmit={handleEdit}>
                  <DialogHeader>
                    <DialogTitle>Edit Benefit Run</DialogTitle>
                    <DialogDescription>
                      Update the date range and notes for Run {run.benefitProgramYear}-{String(run.runNumber).padStart(3, "0")}.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="edit-start">Start Date</Label>
                        <Input id="edit-start" type="date" required value={editStartDate} onChange={(e) => setEditStartDate(e.target.value)} />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="edit-end">End Date</Label>
                        <Input id="edit-end" type="date" required value={editEndDate} onChange={(e) => setEditEndDate(e.target.value)} />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="edit-notes">Notes (optional)</Label>
                      <Textarea id="edit-notes" placeholder="Any additional notes…" rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                    </div>
                    {editError && <p className="text-sm text-destructive">{editError}</p>}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={editing}>
                      {editing && <IconLoader2 className="size-4 animate-spin" />}
                      Save Changes
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Run Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconCalendar className="size-4" /> Date Range
            </div>
            <div className="mt-1 text-sm font-medium">{formatDateRange(run.startDate, run.endDate)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconPackage className="size-4" /> Pay List Batches
            </div>
            <div className="mt-1 text-2xl font-bold">{payLists.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconPlayerPlay className="size-4" /> Total Payments
            </div>
            <div className="mt-1 text-2xl font-bold">{totalPayments.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconCurrencyDollar className="size-4" /> Total Amount
            </div>
            <div className="mt-1 text-2xl font-bold">{formatCurrency(totalAmount)}</div>
          </CardContent>
        </Card>
      </div>

      {/* PSE Pay Lists Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <div>
            <CardTitle className="text-lg">PSE Pay Lists</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Batched vendor payment lists for this run. Payments are split into regular (vendors 44/54) and AMI opt-out (vendors 254/244) groups.
            </p>
          </div>
          {payLists.length === 0 && (
            <Button onClick={handleGenerate} disabled={generating || paymentListV2Loading}>
              {generating ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconBolt className="size-4" />
              )}
              Generate Pay Lists
            </Button>
          )}
          {payLists.length > 0 && payLists.every((pl) => pl.status === 0) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={generating || paymentListV2Loading}>
                  {generating ? (
                    <IconLoader2 className="size-4 animate-spin" />
                  ) : (
                    <IconRefresh className="size-4" />
                  )}
                  Refresh Pay Lists
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Refresh Draft Pay Lists?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will regenerate all Draft pay lists from the current set of certified
                    benefits. Any benefits certified since the last generation will be added.
                    Existing Draft batches will be recreated.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleGenerate}>
                    <IconRefresh className="size-4" />
                    Refresh
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {generateError && (
            <div className="px-6 pb-4">
              <Alert variant="destructive">
                <AlertTitle>Generation Failed</AlertTitle>
                <AlertDescription>{generateError}</AlertDescription>
              </Alert>
            </div>
          )}

          {payListsLoading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : payLists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <IconPackage className="size-8 opacity-50" />
              <p>No pay lists generated yet.</p>
              <p className="text-xs">Click "Generate Pay Lists" to create batches from certified benefits.</p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Batch</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Payments</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[200px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payLists.map((pl) => (
                    <>
                      <TableRow
                        key={pl.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => loadPayListDetail(pl.id)}
                      >
                        <TableCell>
                          {expandedPayListId === pl.id ? (
                            <IconChevronDown className="size-4 text-muted-foreground" />
                          ) : (
                            <IconChevronRight className="size-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">
                            Batch {pl.batchNumber}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={pl.isAmiOptOut ? "destructive" : "outline"}>
                            {pl.isAmiOptOut ? "AMI Opt-Out" : "Regular"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {pl.totalPaymentCount}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {formatCurrency(pl.totalAmount)}
                        </TableCell>
                        <TableCell>
                          <PayListStatusBadge status={pl.status} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => api.download(
                                `/api/pse-pay-lists/${pl.id}/export`,
                                `pse-paysheet-${pl.runNumber}-batch-${pl.batchNumber}.xlsx`
                              )}
                              title="Download Excel"
                            >
                              <IconDownload className="size-3.5" />
                            </Button>
                            {pl.status === 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={updatingPayListId === pl.id}
                                onClick={() => handleStatusUpdate(pl.id, 1)}
                              >
                                {updatingPayListId === pl.id
                                  ? <><IconLoader2 className="size-3.5 animate-spin" /> Finalizing…</>
                                  : <><IconCheck className="size-3.5" /> Finalize</>}
                              </Button>
                            )}
                            {pl.status === 1 && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={updatingPayListId === pl.id}
                                onClick={() => handleStatusUpdate(pl.id, 2)}
                              >
                                {updatingPayListId === pl.id
                                  ? <><IconLoader2 className="size-3.5 animate-spin" /> Submitting…</>
                                  : <><IconSend className="size-3.5" /> Submit</>}
                              </Button>
                            )}
                            {pl.status === 2 && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={updatingPayListId === pl.id}
                                onClick={() => handleStatusUpdate(pl.id, 3)}
                              >
                                {updatingPayListId === pl.id
                                  ? <><IconLoader2 className="size-3.5 animate-spin" /> Updating…</>
                                  : <><IconEdit className="size-3.5" /> Mark Modified</>}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded detail row */}
                      {expandedPayListId === pl.id && (
                        <TableRow key={`${pl.id}-detail`}>
                          <TableCell colSpan={7} className="p-0 bg-muted/30">
                            <PayListDetailPanel
                              detail={payListDetail}
                              loading={detailLoading}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Durable V2 PSE payment-list inclusion. This never edits or reuses legacy draft lists. */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
          <div>
            <CardTitle className="text-lg">Durable PSE Payment List (V2)</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Immutable certified vendor-instruction inclusion. Paid means committed list inclusion,
              not bank settlement or a money transfer.
            </p>
          </div>
          {paymentListV2Capability?.enabled && (
            <div className="flex flex-wrap justify-end gap-2">
              {paymentListV2Operation && (paymentListV2Operation.state === "Failed" || paymentListV2Operation.state === "Orphaned") && (
                <Button variant="outline" onClick={() => void retryPaymentListV2()} disabled={paymentListV2Loading || paymentListV2ScopeBusy}>
                  {paymentListV2Loading ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
                  Retry same operation
                </Button>
              )}
              <Button variant="outline" onClick={startNewPaymentListV2Operation} disabled={paymentListV2Loading}>
                <IconPlus className="size-4" /> New operation
              </Button>
              {!paymentListV2Operation && (
                <Button onClick={() => void generatePaymentListV2()} disabled={paymentListV2Loading || paymentListV2ScopeBusy}>
                  {paymentListV2Loading ? <IconLoader2 className="size-4 animate-spin" /> : <IconBolt className="size-4" />}
                  Generate V2 list
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {paymentListV2Capability && !paymentListV2Capability.enabled && (
            <p className="text-sm text-muted-foreground">Disabled: {paymentListV2Capability.disabledReason}</p>
          )}
          {paymentListV2Error && <p className="text-sm text-destructive">{paymentListV2Error}</p>}
          {paymentListV2Capability?.enabled && !paymentListV2Operation && (
            <div className="grid gap-4 rounded-md border p-4 sm:grid-cols-[minmax(0,1fr)_140px]">
              <div className="space-y-2">
                <Label htmlFor="payment-list-v2-operation-key">Operation key</Label>
                <Input
                  id="payment-list-v2-operation-key"
                  value={paymentListV2OperationKey}
                  disabled={paymentListV2Loading}
                  onChange={(event) => setPaymentListV2OperationKey(event.target.value)}
                  aria-describedby="payment-list-v2-operation-help"
                />
                <p id="payment-list-v2-operation-help" className="text-xs text-muted-foreground">
                  Keep this key when a request times out. Retrying it safely returns the same operation.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={paymentListV2ExplicitReExport}
                    disabled={paymentListV2Loading}
                    onCheckedChange={(checked) => setPaymentListV2ExplicitReExport(checked === true)}
                  />
                  Explicit re-export of existing immutable membership
                </label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment-list-v2-batch-size">Max instructions/batch</Label>
                <Input
                  id="payment-list-v2-batch-size"
                  type="number"
                  min={1}
                  max={1000}
                  value={paymentListV2BatchSize}
                  disabled={paymentListV2Loading}
                  onChange={(event) => setPaymentListV2BatchSize(event.target.value)}
                />
                <Button className="w-full" onClick={() => void generatePaymentListV2()} disabled={paymentListV2Loading || paymentListV2ScopeBusy || !paymentListV2OperationKey.trim()}>
                  {paymentListV2Loading ? <IconLoader2 className="size-4 animate-spin" /> : <IconBolt className="size-4" />}
                  Create operation
                </Button>
              </div>
            </div>
          )}
          {paymentListV2Operation && (
            <div className="space-y-3 rounded-md border p-4">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={paymentListV2Operation.state === "Committed" ? "default" : paymentListV2Operation.state === "Failed" || paymentListV2Operation.state === "Orphaned" ? "destructive" : "outline"}>
                  {paymentListV2Operation.state}
                </Badge>
                <span className="font-mono text-xs">{paymentListV2Operation.operationId}</span>
                {paymentListV2Operation.explicitReExport && <Badge variant="secondary">Explicit re-export</Badge>}
              </div>
              <dl className="grid gap-2 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Operation key</dt><dd className="break-all font-mono text-xs">{paymentListV2Operation.operationKey}</dd></div>
                <div><dt className="text-muted-foreground">Frozen run scope</dt><dd>{formatDateRange(paymentListV2Operation.runStartDate, paymentListV2Operation.runEndDate)}</dd></div>
                <div><dt className="text-muted-foreground">Instructions / batches</dt><dd>{paymentListV2Operation.candidateCount} / {paymentListV2Operation.lists.length}</dd></div>
                <div><dt className="text-muted-foreground">Committed</dt><dd>{paymentListV2Operation.committedAt ? formatDate(paymentListV2Operation.committedAt) : "Not committed"}</dd></div>
              </dl>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => void refreshPaymentListV2()} disabled={paymentListV2Loading}>
                  <IconRefresh className="size-3.5" /> Refresh status
                </Button>
                {paymentListV2Operation.state === "Committed" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void api.download(
                      `/api/pse-pay-lists/v2/operations/${paymentListV2Operation.operationId}/artifact`,
                      paymentListV2Operation.artifactFileName ?? `pse-payment-list-v2-${run.benefitProgramYear}.zip`
                    )}
                  >
                    <IconDownload className="size-3.5" /> Download immutable artifact
                  </Button>
                )}
                {paymentListV2Operation.failureCode && <span className="text-sm text-destructive">{paymentListV2Operation.failureCode}</span>}
              </div>
            </div>
          )}
          {paymentListV2History.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Operation history</h3>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader><TableRow><TableHead>Created</TableHead><TableHead>Operation</TableHead><TableHead>State</TableHead><TableHead>Scope</TableHead><TableHead /></TableRow></TableHeader>
                  <TableBody>
                    {paymentListV2History.map((item) => (
                      <TableRow key={item.operationId}>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(item.createdAt)}</TableCell>
                        <TableCell className="max-w-[240px] truncate font-mono text-xs" title={item.operationKey}>{item.operationKey}</TableCell>
                        <TableCell><Badge variant={item.state === "Committed" ? "default" : item.state === "Failed" || item.state === "Orphaned" ? "destructive" : "outline"}>{item.state}</Badge></TableCell>
                        <TableCell className="text-xs">{item.candidateCount} instructions · {item.maxBatchSize}/batch</TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => setPaymentListV2Operation(item)}>View</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Pay List Detail Panel (expanded row)
// ═══════════════════════════════════════════════════════════════════════════════

function PayListDetailPanel({
  detail,
  loading,
}: {
  detail: PsePayListDetail | null
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="p-4 space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    )
  }

  if (!detail || detail.items.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No line items in this pay list.
      </div>
    )
  }

  const vendorLabel = (id: number) => {
    switch (id) {
      case 44: return "Gas (44)"
      case 54: return "Electric (54)"
      case 244: return "AMI Gas (244)"
      case 254: return "AMI Electric (254)"
      default: return `Vendor ${id}`
    }
  }

  return (
    <div className="border-t">
      <div className="px-6 py-3 flex items-center justify-between bg-muted/20">
        <span className="text-sm font-medium">
          {detail.items.length} line items · {formatCurrency(detail.totalAmount)} total
        </span>
        {detail.notes && (
          <span className="text-xs text-muted-foreground">Note: {detail.notes}</span>
        )}
      </div>
      <div className="max-h-[400px] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow className="text-xs">
              <TableHead>#</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead className="text-xs">Benefit ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.items.map((item, idx) => (
              <TableRow key={item.id} className="text-xs">
                <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                <TableCell className="font-mono">{item.accountNumber}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {vendorLabel(item.vendorId)}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono">{formatCurrency(item.paymentAmount)}</TableCell>
                <TableCell className="font-mono text-muted-foreground text-[10px]">
                  {item.benefitId.slice(0, 8)}…
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
